import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { TUI_DARK_MODE, TuiRoot } from '@taiga-ui/core';
import { AsyncPipe, NgClass } from '@angular/common';

import { SwUpdate } from '@angular/service-worker';
import { TuiPushService } from '@taiga-ui/kit';
import { EMPTY, filter, map, mergeMap, Subject, takeUntil } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  DeviceDetectorService,
  NetworkService,
  XAlertService,
} from '@core/services';
import { isRestrictedAppPath, isWorkflowAppPath } from '@core/helpers';
import { AccountService } from '@core/services/account/account.service';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';
import { ThemeService } from '@core/services/theme/theme.service';
import { MainLayoutComponent } from '@core/components';
import { AppVersionService } from '@core/services/app-version.service';
import { OfflineQueueService } from '@core/services/offline-queue.service';
import { PhotoQueueService } from '@core/services/workflow/photo-queue.service';
import { WorkflowStorageMigrationService } from '@core/services/workflow/storage/workflow-storage-migration.service';
import * as Sentry from '@sentry/angular';
import { FeatureFlagsService } from '@core/services/feature-flags.service';

@Component({
  selector: 'x-root',
  templateUrl: './app.component.html',
  imports: [RouterModule, TuiRoot, AsyncPipe, MainLayoutComponent, NgClass],
  host: {
    '[attr.theme]': '$theme()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  protected readonly permissionService = inject(OutkeepPermissionService);
  private readonly accountService = inject(AccountService);
  private readonly push$ = inject(TuiPushService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly xAlertService = inject(XAlertService);
  private readonly networkService = inject(NetworkService);
  private readonly appVersionService = inject(AppVersionService);
  private readonly offlineQueueService = inject(OfflineQueueService);
  private readonly photoQueueService = inject(PhotoQueueService);
  private readonly workflowStorageMigration = inject(
    WorkflowStorageMigrationService,
  );
  private readonly featureFlags = inject(FeatureFlagsService);
  private isOnline = true;
  readonly deviceService = inject(DeviceDetectorService);
  readonly themeService = inject(ThemeService);
  private readonly darkMode = inject(TUI_DARK_MODE);
  private readonly router = inject(Router);
  private readonly currentPathname = this.document.location.pathname;
  public $isWorkflowRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => isWorkflowAppPath(event.urlAfterRedirects)),
    ),
    { initialValue: isWorkflowAppPath(this.currentPathname) },
  );
  public $isRestrictedRequestRoute = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => isRestrictedAppPath(event.urlAfterRedirects)),
    ),
    { initialValue: isRestrictedAppPath(this.currentPathname) },
  );
  readonly user$ = this.accountService.currentUser$;
  readonly authState$ = this.accountService.authState$;
  readonly $theme = signal<'light' | 'dark'>('light');

  ngOnInit() {
    this.checkForUpdates();
    this.checkNetworkStatus();
    if (this.featureFlags.workflowsEnabled) {
      // Процессоры очередей стартуют после разовой миграции старых записей без владельца
      void this.workflowStorageMigration
        .migrateLegacyStorage()
        .catch(error => {
          console.error('Workflow storage migration failed:', error);
          Sentry.captureException(error);
        })
        .finally(() => {
          this.offlineQueueService.startQueueProcessor(); // Start offline queue processor for workflow submissions
          this.photoQueueService.startProcessor();
        });
    }
    this.appVersionService
      .loadVersion()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
    this.themeService.theme$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(mode => {
        const theme =
          mode === 'system'
            ? window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'dark'
              : 'light'
            : mode;

        this.$theme.set(theme);
        this.darkMode.set(theme === 'dark');
      });
  }

  private checkNetworkStatus() {
    this.networkService.isOnline$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(isOnlineNow => {
        if (!this.isOnline && isOnlineNow) {
          this.xAlertService.success('Подключение к интернету восстановлено');
          this.networkService.clearLastOnlineStatus();
          // Trigger queue processing when connection is restored
          if (this.featureFlags.workflowsEnabled) {
            this.offlineQueueService
              .manualSync()
              .then(() => {
                console.log('Offline queue sync completed');
              })
              .catch(err => {
                console.error('Error syncing offline queue:', err);
              });
          }
        }
        this.isOnline = isOnlineNow;
        if (!isOnlineNow) {
          this.xAlertService.warn('Вы оффлайн, данные могут быть устаревшими');
          const lastOnlineStatus = this.networkService.getLastOnlineStatus();

          if (lastOnlineStatus) {
            this.xAlertService.showMessage(
              `Последний раз вы были онлайн: ${lastOnlineStatus}`,
            );
          }
        }
      });
  }

  private checkForUpdates() {
    if (this.swUpdate.isEnabled) {
      const closeDetected$ = new Subject<void>();

      this.destroyRef.onDestroy(() => closeDetected$.complete());

      this.swUpdate.versionUpdates
        .pipe(
          mergeMap(event => {
            const { type } = event;

            switch (type) {
              case 'VERSION_DETECTED':
                return this.push$
                  .open('', {
                    type: 'Обновление',
                    heading: 'Доступно обновление! Загрузка...',
                    icon: '@tui.download-cloud',
                  })
                  .pipe(takeUntil(closeDetected$));

              case 'VERSION_READY':
                closeDetected$.next();

                return this.push$.open('', {
                  type: 'Обновление',
                  heading: 'Приложение готово к обновлению!',
                  icon: '@tui.check',
                  buttons: ['Обновить'],
                });
              case 'VERSION_INSTALLATION_FAILED':
                console.warn('VERSION_INSTALLATION_FAILED', event);
                Sentry.captureException(event.error);
                closeDetected$.next();

                return this.push$.open(
                  'Попробуйте перезагрузить страницу и попробовать заново!',
                  {
                    type: 'Обновление',
                    heading: 'Произошла ошибка при обновлении!',
                    icon: '@tui.circle-alert',
                    buttons: ['Перезагрузить'],
                  },
                );
              default:
                return EMPTY;
            }
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(event => {
          if (event === 'Обновить' || event === 'Перезагрузить') {
            this.document.location.reload();
          }
        });

      this.swUpdate.unrecoverable
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(event => {
          console.warn('Unrecoverable error with cache', event);
          Sentry.captureException(event.reason);
          this.xAlertService.warn(
            'Пожалуйста, перезагрузите страницу',
            'Произошла ошибка с кешем, которую мы не можем восстановить',
          );
        });
    }
  }
}
